import React, { useState, useEffect, useRef } from 'react';
import Popup, { ToolbarItem as PopupToolbarItem } from 'devextreme-react/popup';
import { CheckBox } from 'devextreme-react/check-box';
import TextArea from 'devextreme-react/text-area';
import DataGrid, { Column, Editing, Scrolling, Selection, Sorting, FilterRow, ColumnChooser, Export, Lookup } from 'devextreme-react/data-grid';
import { WMSInventoryUOMFlags } from '../../../../../../const/constants';
import { MESSAGES } from '../../../../../../common/Messages';

// WMD0114 - "Replicate Shipment Item Details Based on Manual Lot Allocation".
//
// Opens after the replicated SHIPMENT header is saved, when the WMD0113 Lot choice was
// "Replicate Existing Lot, If Available" or "Manually Select Lot - Leave Lot Code Blank".
// The sibling WMD0115 handles "Auto Select Lot".
//
// Unlike WMD0115 this is lot-aware: the grid shows the source lot and its availability, and on OK
// the lot is carried onto the new line (or dropped, for "leave lot code blank").

const helpText =
    'The Original Replicate Shipment Transaction has the following items. Please enter the Quantity ' +
    'you wish to ship for each line Item and Click the OK button to create the "Transaction Detail" ' +
    'record(s). The quantity entered in this screen will be defaulted to the Order & Actual Quantity ' +
    'fields in the transaction detail record based on the stage in this transaction.';

const ReplicateShipmentManualLot = ({ visible, onClose, onOk, items }) => {
    const [rows, setRows] = useState([]);

    // Mirrors `rows`. saveEditData() below writes the open cell back through onRowUpdated, but a
    // setState from that handler is not visible to the same tick - reading state straight after the
    // await would still return the pre-edit value, which is the bug this guards.
    const rowsRef = useRef([]);
    const applyRows = (next) => {
        rowsRef.current = next;
        setRows(next);
    };

    const dataGrid = useRef(null);

    // The focused row is held by key, not as a copy: the Comment box below writes back into `rows`,
    // and a copy would go stale the moment it did.
    const [focusedKey, setFocusedKey] = useState(null);
    const focusedRow = rows.find(r => r.__key === focusedKey) ?? null;

    // Reloaded each time the dialog opens, so a previous run's edits are never reused.
    useEffect(() => {
        if (!visible) return;
        const mapped = (items || []).map((item, index) => ({ ...item, __key: index }));
        applyRows(mapped);
        setFocusedKey(mapped.length > 0 ? mapped[0].__key : null);
    }, [visible, items]);

    const allSelected = rows.length > 0 && rows.every(r => r.selected);

    // WMD0114 zeroes ORDERED on an untick, not Ship Quantity - its handler's own comment is "set the
    // order quantity when uncheck" (uWMD0114.PAS:271-287). WMD0115 zeroes Ship Quantity instead, so the
    // two dialogs genuinely differ here. Either way it is display feedback only: an unticked row is not
    // inserted at all.
    const clearOnUntick = (row) => ({ ...row, selected: false, totalQuantityOrder: 0 });

    const toggleAll = (value) => {
        // SelectAll routes every row through the same toggle handler, so unticking all clears Ordered
        // on all of them.
        applyRows(rows.map(r => (value ? { ...r, selected: true } : clearOnUntick(r))));
    };

    const onRowUpdating = (e) => {
        if (e.newData.selected === false) {
            e.newData.totalQuantityOrder = 0;
        }
    };

    // Writes the edited comment back to the focused row, so it travels with the rest of that line.
    const updateComment = (value) => {
        if (focusedKey === null) return;
        applyRows(rowsRef.current.map(r => (r.__key === focusedKey ? { ...r, comment: value } : r)));
    };

    // Delphi disables OK outright while a row is being edited (BeforeEdit / AfterPost), so the edit
    // can never be in flight when OK is pressed. Committing it instead reaches the same outcome -
    // no lost keystroke - without taking the button away mid-type. Same approach as
    // CommonReport.commitPendingGridEdits, which exists for exactly this failure.
    const handleOk = async () => {
        const grid = dataGrid.current;
        if (grid) {
            await grid.instance().saveEditData();
        }

        if (onOk) onOk(rowsRef.current.map(({ __key, ...row }) => row));
    };

    return (
        <Popup
            visible={visible}
            onHiding={onClose}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title="Replicate Shipment Item Details Based on Manual Lot Allocation (WMD0114)"
            container=".dx-viewport"
            width="85%"
            height="75%"
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div
                    style={{
                        border: '1px solid #999',
                        padding: '8px 10px',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        marginBottom: '10px',
                    }}
                >
                    {helpText}
                </div>

                <div style={{ flex: 1, minHeight: 0 }}>
                    <DataGrid
                        ref={dataGrid}
                        dataSource={rows}
                        keyExpr="__key"
                        showBorders={true}
                        rowAlternationEnabled={true}
                        allowColumnResizing={true}
                        onExporting={(e) => {
                            if (window.handleDevExtremeDataGridExport) {
                                window.handleDevExtremeDataGridExport(e);
                            }
                        }}
                        columnAutoWidth={true}
                        height="100%"
                        onRowUpdating={onRowUpdating}
                        onRowUpdated={(e) => applyRows(rowsRef.current.map(r => (r.__key === e.key ? { ...r, ...e.data } : r)))}
                        onFocusedRowChanged={(e) => setFocusedKey(e.row?.data?.__key ?? null)}
                        focusedRowEnabled={true}
                    >
                        <Editing mode="cell" allowUpdating={true} />
                        <Scrolling mode="virtual" />
                        <Selection mode="none" />
                        <Sorting mode="multiple" />
                        <FilterRow visible={true} />
                        <ColumnChooser enabled={true} />
                        <Export enabled={true} />

                        <Column dataField="selected" caption="" dataType="boolean" width={50} />
                        <Column dataField="itemCode" caption="Item" allowEditing={false} />
                        <Column dataField="itemAvailable" caption="Item Available" dataType="number" alignment="right" allowEditing={false} format={{ type: "fixedPoint", precision: 2 }} />
                        <Column dataField="lotCode" caption="Lot" allowEditing={false} />
                        <Column dataField="lotAvailable" caption="Lot Available" dataType="number" alignment="right" allowEditing={false} format={{ type: "fixedPoint", precision: 2 }} />
                        <Column dataField="inventoryUomFlag" caption="Trans Unit" allowEditing={false}>
                            {/* I / P / K / H are stored codes; the operator sees the description. */}
                            <Lookup dataSource={WMSInventoryUOMFlags} displayExpr="Text" valueExpr="ID" />
                        </Column>
                        <Column dataField="totalQuantityOrder" caption="Ordered" dataType="number" alignment="right" allowEditing={false} format={{ type: "fixedPoint", precision: 2 }} />
                        <Column dataField="totalQuantityActual" caption="Actual" dataType="number" alignment="right" allowEditing={false} format={{ type: "fixedPoint", precision: 2 }} />
                        {/* The only editable column, as in Delphi where the cursor tabs straight to it. */}
                        <Column dataField="shipQuantity" caption="Ship Quantity" dataType="number" alignment="right" allowEditing={true} format={{ type: "fixedPoint", precision: 2 }} />
                    </DataGrid>
                </div>

                {/* The Descriptions panel, which follows the focused row. */}
                <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', paddingTop: '10px' }}>
                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                backgroundColor: window._env_.Color2,
                                color: '#FFFFFF',
                                fontWeight: 'bold',
                                fontSize: '13px',
                                textAlign: 'center',
                                padding: '3px',
                                marginBottom: '6px',
                            }}
                        >
                            Descriptions
                        </div>
                        <div style={{ display: 'flex', marginBottom: '4px' }}>
                            <label style={{ width: '110px', fontWeight: 'bold', fontSize: '12px' }}>Item Desc - 1</label>
                            <span style={{ fontSize: '12px' }}>{focusedRow?.itemDescription1 ?? ''}</span>
                        </div>
                        <div style={{ display: 'flex', marginBottom: '4px' }}>
                            <label style={{ width: '110px', fontWeight: 'bold', fontSize: '12px' }}>Lot Desc - 1</label>
                            <span style={{ fontSize: '12px' }}>{focusedRow?.lotDescription1 ?? ''}</span>
                        </div>
                        {/* Editable, matching edtMemo - a TAbMemo bound to COMMENT with no ReadOnly.
                            What the user types here is what is written to the new detail line. */}
                        <div style={{ display: 'flex' }}>
                            <label style={{ width: '110px', fontWeight: 'bold', fontSize: '12px' }}>Comment</label>
                            <TextArea
                                value={focusedRow?.comment ?? ''}
                                disabled={!focusedRow}
                                height={60}
                                stylingMode="outlined"
                                autoResizeEnabled={false}
                                onValueChanged={(e) => updateComment(e.value)}
                                width="100%"
                            />
                        </div>
                    </div>

                    <div style={{ paddingTop: '28px' }}>
                        <CheckBox
                            value={allSelected}
                            text={allSelected ? 'Unselect All' : 'Select All'}
                            onValueChanged={(e) => toggleAll(e.value)}
                        />
                    </div>
                </div>
            </div>

            <PopupToolbarItem
                toolbar="top"
                location="after"
                widget="dxButton"
                options={{
                    text: MESSAGES.OK ?? 'OK',
                    type: 'default',
                    stylingMode: 'contained',
                    elementAttr: { class: 'accordianButton' },
                    onClick: handleOk,
                }}
            />
        </Popup>
    );
};

export default ReplicateShipmentManualLot;
